import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import { useHistory } from "react-router-dom";

import { Button, CreateAlert, Icon, ITableStructureItem } from "../../modules";
import {
  TableFilterInputText,
  TableFilterRangeTimeInput,
} from "../../components/table-filter-inputs";
import { FirmwareService } from "../../services/firmware";
import { TableSimplePagination } from "../../modules/table/TableSimplePagination";
import { TableFilterInputSelect } from "../../components/table-filter-inputs/select";
import { ConfirmDeletePopup } from "../../components/confirm-delete-popup";
import ImportButton from "../../components/import-button";

const FirmwareStatus = [
  {
    label: "Tất cả",
    value: "ALL",
  },
  {
    label: "Đang chờ",
    value: "NOT_CONNECTED",
  },
  {
    label: "Đã kích hoạt",
    value: "CONNECTED",
  },
  {
    label: "Đã khoá",
    value: "LOCKED",
  },
];

// const formatToIP = (ip: any) => {
//   if (!ip) {
//     return "--";
//   }

//   const numStr = ip?.toString();
//   const ipSegments = numStr.match(/\d{1,3}(?=(\d{3})*$)/g);
//   return ipSegments?.join(".");
// };

const Firmwares = () => {
  const history = useHistory();

  const [dataList, setDataList] = useState<any>();
  const [filterParams, setFilterParams] = useState<any>({
    page: 1,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);
  const [activeDelete, setActiveDelete] = useState<{
    open: boolean;
    data: any;
  }>({
    open: false,
    data: undefined,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    let paramsFilter = filterParams;

    if (!filterParams?.fromDate && filterParams?.toDate) {
      paramsFilter = {
        ...filterParams,
        fromDate: "1970-01-01 00:00:00",
      };
    }

    if (!filterParams?.toDate && filterParams?.fromDate) {
      paramsFilter = {
        ...filterParams,
        toDate: moment().format("YYYY-MM-DD HH:mm:ss"),
      };
    }

    const data = await FirmwareService.getList(paramsFilter);
    setDataList(data);
    setLoading(false);
  }, [filterParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (data: any) => {
    try {
      const res = await FirmwareService.delete(data?.data?.firmwareId);

      if (res?.success) {
        setActiveDelete({ open: false, data: undefined });
        const _params = { page: 1, pageSize: 10 };
        setFilterParams({ ..._params });
        CreateAlert({
          type: "success",
          message: "Xóa dữ liệu thành công",
        });
      }
    } catch (error: any) {
      setActiveDelete({ open: false, data: undefined });
      CreateAlert({
        type: "danger",
        message: error.message || "Xóa dữ liệu thất bại",
      });
    }
  };

  const structure: ITableStructureItem[] = [
    {
      name: "Mã firmware",
      key: "code",
      render: (item: any) => item?.code || "--",
    },
    {
      name: "Version",
      key: "version",
      render: (item: any) => item?.version || "--",
    },
    {
      name: "Mac",
      key: "mac",
      render: (item: any) => item?.mac || "--",
    },
    {
      name: "Mac wifi",
      key: "macWifi",
      render: (item: any) => item?.macWifi || "--",
    },
    {
      name: "Trạng thái",
      key: "isActive",
      render: (item: any) => (item?.isActive ? "Đang kết nối" : "Chưa kết nối"),
    },
    {
      name: "Tình trạng",
      key: "status",
      render: (item: any) =>
        item?.status
          ? FirmwareStatus?.find((itemFind) => itemFind?.value === item?.status)?.label
          : "--",
    },
    {
      name: "IP",
      key: "ip",
      render: (item: any) => item?.ip || "--",
    },
    {
      name: "Ngày tạo",
      key: "created",
      render: (item: any) =>
        item?.created
          ? new Intl.DateTimeFormat("en-GB").format(new Date(item?.created))
          : "--",
    },
    {
      name: "Ngày khóa",
      key: "lockedAt",
      render: (item: any) =>
        item?.lockedAt
          ? new Intl.DateTimeFormat("en-GB").format(new Date(item?.lockedAt))
          : "--",
    },
    {
      name: "Ngày kích hoạt",
      key: "createActive",
      render: (item: any) =>
        item?.createActive
          ? new Intl.DateTimeFormat("en-GB").format(new Date(item?.createActive))
          : "--",
    },
    {
      name: "Ngày ngưng kích hoạt",
      key: "inactiveAt",
      render: (item: any) =>
        item?.inactiveAt
          ? new Intl.DateTimeFormat("en-GB").format(new Date(item?.inactiveAt))
          : "--",
    },
    {
      name: "Ngày bảo hành",
      key: "warranty",
      render: (item: any) =>
        item?.warranty
          ? new Intl.DateTimeFormat("en-GB").format(new Date(item?.warranty))
          : "--",
    },
    {
      name: "Thao tác",
      render: (item: any) => {
        return (
          <div className="btnGroup">
            <span
              className="btn btn-edit"
              onClick={() => history.push(`/firmwares/${item?.firmwareId}`)}
            >
              <Icon.Edit />
            </span>

            {/* <span
              className="btn"
              onClick={() =>
                setActiveDelete({
                  open: true,
                  data: item,
                })
              }
            >
              <Icon.Delete />
            </span> */}
          </div>
        );
      },
    },
  ];

  const handleExportExcel = async () => {
    return new Promise(async (resolve) => {
      try {
        let params = filterParams;

        if (!params?.fromDate && params?.toDate) {
          params = {
            ...filterParams,
            fromDate: "1970-01-01 00:00:00",
          };
        }

        if (!params?.toDate && params?.fromDate) {
          params = {
            ...filterParams,
            toDate: moment().format("YYYY-MM-DD HH:mm:ss"),
          };
        }

        const response = await FirmwareService.getList({
          ...params,
          page: 1,
          pageSize: 1000000,
        });

        const data = response.data;

        const fileHead = structure.map((v) => v.name);
        const dataExport = [
          fileHead,
          ...data.map((item: any) =>
            structure.map((column: any, index) => {
              if (!column.key) return "";
              if (column.key === "isActive") {
                return item[column.key] ? "Đang kết nối" : "Chưa kết nối";
              }
              if (column.key === "status") {
                return item[column.key]
                  ? FirmwareStatus?.find(
                    (itemFind) => itemFind?.value === item[column.key]
                  )?.label
                  : "--";
              }
              if (["created", "modified", "warranty"].includes(column.key)) {
                return moment(item[column.key]).format("YYYY-MM-DD HH:mm:ss");
              }
              return item[column.key];
            })
          ),
        ];

        const ws = XLSX.utils.aoa_to_sheet(dataExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "SheetJS");

        const now = new Date();
        XLSX.writeFile(
          wb,
          `Firmware List ${now.toLocaleDateString().replace(/\//g, "-")} ${now
            .toLocaleTimeString()
            .replace(/:/g, "-")}.xlsx`,
          { type: "binary" }
        );

        resolve(
          CreateAlert({
            type: "success",
            message: "Xuất dữ liệu thành công",
          })
        );
      } catch (error: any) {
        resolve(
          CreateAlert({
            type: "danger",
            message: error.message || "Xuất dữ liệu thất bại",
          })
        );
      }
    });
  };

  const handleImport = async (form: FormData, cb: any) => {
    try {
      const res: any = await FirmwareService.import(form);

      if (res?.success) {
        const _params = { page: 1, pageSize: 10 };
        setFilterParams({ ..._params });
        cb();
        CreateAlert({
          type: "success",
          message: "Nhập dữ liệu thành công",
        });
      }
    } catch (error: any) {
      cb();
      CreateAlert({
        type: "danger",
        message: error.message || "Nhập dữ liệu thất bại",
      });
    }
  };

  return (
    <>
      <div className="firmwares-container">
        <div className="title">Quản lý thiết bị</div>

        <div className="content">
          <div className="wrap-action-header">
            <ImportButton
              fileUrl="/templates/Template_import-firmware.xlsx"
              onImport={handleImport}
            />

            <Button
              className="mb20"
              label="Xuất excel"
              buttonType="success"
              onClick={handleExportExcel}
            />

            <Button
              className="mb20"
              label="Tạo mới"
              buttonType="success"
              onClick={() => history.push("/firmwares/create")}
            />
          </div>

          <TableSimplePagination
            dataTable={{
              dataList: dataList,
              filterParams: filterParams,
              totalPage: dataList
                ? Math.ceil(dataList?.count / filterParams?.pageSize)
                : 0,
              loading,
            }}
            setParams={setFilterParams}
            filters={[
              {
                name: "Tìm kiếm theo mã firmware",
                key: "searchString",
                input: (s: any) => (
                  <TableFilterInputText
                    {...s}
                    params={
                      typeof filterParams?.searchString !== "undefined"
                        ? {
                          ...s?.params,
                          searchString: filterParams?.searchString,
                        }
                        : { ...s?.params }
                    }
                  />
                ),
              },
              {
                name: "Kích hoạt từ ngày",
                input: (e: any) => (
                  <TableFilterRangeTimeInput
                    {...e}
                    fromKey="fromDate"
                    toKey="toDate"
                    params={{
                      ...e?.params,
                      fromDate:
                        moment(
                          filterParams?.fromDate,
                          "YYYY-MM-DD HH:mm:ss"
                        ).unix() * 1000,
                      toDate:
                        moment(
                          filterParams?.toDate,
                          "YYYY-MM-DD HH:mm:ss"
                        ).unix() * 1000,
                    }}
                    onChange={(e) => {
                      if (e?.fromDate && e?.toDate) {
                        setFilterParams((prev: any) => ({
                          ...prev,
                          fromDate: moment(e?.fromDate).format(
                            "YYYY-MM-DD HH:mm:ss"
                          ),
                          toDate: moment(e?.toDate).format(
                            "YYYY-MM-DD HH:mm:ss"
                          ),
                        }));
                        return;
                      }

                      if (e?.fromDate) {
                        setFilterParams((prev: any) => ({
                          ...prev,
                          fromDate: moment(e?.fromDate).format(
                            "YYYY-MM-DD HH:mm:ss"
                          ),
                          toDate: "",
                        }));
                        return;
                      }

                      if (e?.toDate) {
                        setFilterParams((prev: any) => ({
                          ...prev,
                          toDate: moment(e?.toDate).format(
                            "YYYY-MM-DD HH:mm:ss"
                          ),
                          fromDate: "",
                        }));
                        return;
                      }

                      setFilterParams((prev: any) => ({
                        ...prev,
                        fromDate: "",
                        toDate: "",
                      }));
                    }}
                  />
                ),
              },
              {
                name: "Tình trạng",
                key: "status",
                input: (s: any) => (
                  <TableFilterInputSelect
                    {...s}
                    params={
                      typeof filterParams?.status !== "undefined"
                        ? { ...s?.params, status: filterParams?.status }
                        : { ...s?.params }
                    }
                    options={FirmwareStatus}
                  />
                ),
              },
              {
                name: "Trạng thái",
                key: "isActive",
                input: (s: any) => (
                  <TableFilterInputSelect
                    {...s}
                    params={
                      typeof filterParams?.isActive !== "undefined"
                        ? { ...s?.params, isActive: filterParams?.isActive }
                        : { ...s?.params }
                    }
                    options={[
                      {
                        label: "Tất cả",
                        value: "ALL",
                      },
                      {
                        label: "Đang kết nối",
                        value: true,
                      },
                      {
                        label: "Chưa kết nối",
                        value: false,
                      },
                    ]}
                  />
                ),
              },
              {
                name: "Làm mới",
                input: () => (
                  <Button
                    label="Xác nhận"
                    buttonType="info"
                    onClick={() => setFilterParams({ page: 1, pageSize: 10 })}
                  />
                ),
              },
            ]}
            structure={structure}
          />
        </div>
      </div>

      {activeDelete?.open && (
        <ConfirmDeletePopup
          onClose={() => setActiveDelete({ open: false, data: undefined })}
          onConfirm={() => handleDelete(activeDelete)}
        />
      )}
    </>
  );
};

export default Firmwares;
